// (c) chaehongee. Confidential and proprietary.
import { useEffect, useMemo, useRef, useState } from 'react'
import dagre from 'dagre'
import type { Rect } from '../types'
import { useAppSelector } from '../../../store/hooks/hooks'

interface Params {
    session: any
    imageSizesBySrc: Record<string, { w: number; h: number }>
    canvasItems: Array<{ id: string; src: string }>
    containerRef: React.RefObject<HTMLDivElement | null>
    worldRectsById: Record<string, Rect>
    setWorldRects: (rects: Record<string, Rect>) => void
    setContentScale: (scale: number) => void
    setContentOffset: (offset: { x: number; y: number }) => void
    currentScale?: number
    currentOffset?: { x: number; y: number }
    centerOnAdd?: boolean
    sessionId?: string | null
}

export function useVerticalTreeLayout({
    session,
    imageSizesBySrc,
    canvasItems,
    containerRef,
    worldRectsById,
    setWorldRects,
    setContentScale,
    setContentOffset,
    currentScale,
    currentOffset,
    centerOnAdd = false,
    sessionId,
}: Params): { ready: boolean; applyLayout: () => void } {
    const [ready, setReady] = useState<boolean>(false)
    const [needsLayout, setNeedsLayout] = useState<boolean>(false)
    const [layoutMode, setLayoutMode] = useState<'fill-missing' | 'full'>('fill-missing')

    // Get saved manual positions from Redux
    const savedPositions = useAppSelector((state) =>
        sessionId ? state.canvasPositions.positions[sessionId] : null
    )

    const getIdsNeedingPlacement = useMemo(() => {
        const EPS = 0.5
        const isPlaceholder = (w: number, h: number) =>
            Math.abs(w - 512) < EPS && Math.abs(h - 512) < EPS

        return () => {
            const missing: string[] = []
            const brokenPlaceholderAuto: string[] = []
            for (const it of canvasItems) {
                const saved = savedPositions?.[it.id]
                if (!saved) {
                    missing.push(it.id)
                    continue
                }
                if (saved.manuallySet) continue
                // Repair path: if we previously persisted a placeholder-based rect (512x512)
                // and we now know the natural size is different, re-place it.
                if (isPlaceholder(saved.w, saved.h)) {
                    const sz = imageSizesBySrc[it.src]
                    if (sz && (Math.abs(sz.w - saved.w) > 1 || Math.abs(sz.h - saved.h) > 1)) {
                        brokenPlaceholderAuto.push(it.id)
                    }
                }
            }
            return { missing, brokenPlaceholderAuto }
        }
    }, [canvasItems, savedPositions, imageSizesBySrc])

    // Compute Dagre layout rects with vertical stacking
    const computeLayoutRects = useMemo(() => {
        return () => {
            if (!session?.nodes) return {}
            const g = new dagre.graphlib.Graph()
            g.setGraph({
                rankdir: 'TB',
                ranker: 'tight-tree',
                ranksep: 260,
                nodesep: 40,
                edgesep: 20,
            } as any)
            g.setDefaultEdgeLabel(() => ({}))
            // Layout only the nodes that are actually rendered as canvas items.
            // This avoids persisting / fitting against nodes that never appear on the canvas.
            const canvasSrcById = new Map(canvasItems.map((it) => [it.id, it.src]))
            const canvasIdSet = new Set(canvasItems.map((it) => it.id))

            const sizeById: Record<string, { w: number; h: number }> = {}
            const layoutNodes = (session.nodes as any[]).filter((n) => canvasIdSet.has(n.id))
            for (const n of layoutNodes) {
                const src = canvasSrcById.get(n.id)
                const sz = src ? imageSizesBySrc[src] : undefined
                sizeById[n.id] = sz ? { w: sz.w, h: sz.h } : { w: 512, h: 512 }
                g.setNode(n.id, { width: sizeById[n.id].w, height: sizeById[n.id].h })
            }
            for (const c of (session?.commits as any[]) ?? []) {
                const target = c.node_to_id as string
                if (!canvasIdSet.has(target)) continue
                const sources = (c.nodes_from_ids as string[]) ?? []
                for (const s of sources) {
                    if (!canvasIdSet.has(s)) continue
                    if (!sizeById[s] || !sizeById[target]) continue
                    g.setEdge(s, target)
                }
            }
            try { dagre.layout(g) } catch { /* ignore */ }
            let minX = Number.POSITIVE_INFINITY
            let minY = Number.POSITIVE_INFINITY
            const raw: Record<string, { x: number; y: number; w: number; h: number }> = {}
            for (const n of layoutNodes) {
                const pos = g.node(n.id)
                const size = sizeById[n.id]
                if (!pos || !size) continue
                const x = pos.x - size.w / 2
                const y = pos.y - size.h / 2
                raw[n.id] = { x, y, w: size.w, h: size.h }
                if (x < minX) minX = x
                if (y < minY) minY = y
            }
            if (!isFinite(minX)) minX = 0
            if (!isFinite(minY)) minY = 0
            const normalized: Record<string, { x: number; y: number; w: number; h: number }> = {}
            for (const id of Object.keys(raw)) {
                const r = raw[id]
                normalized[id] = { x: r.x - minX, y: r.y - minY, w: r.w, h: r.h }
            }
            return normalized
        }
    }, [session?.nodes, session?.commits, imageSizesBySrc, canvasItems])

    const getMissingPositionIds = useMemo(() => {
        return () => {
            if (!canvasItems.length) return []
            const missing: string[] = []
            for (const it of canvasItems) {
                if (!savedPositions?.[it.id]) missing.push(it.id)
            }
            return missing
        }
    }, [canvasItems, savedPositions])

    // Trigger layout when graph structure changes (nodes/edges)
    const prevGraphCountsRef = useRef<{ n: number; e: number }>({ n: 0, e: 0 })
    useEffect(() => {
        const n = Array.isArray(session?.nodes) ? session!.nodes.length : 0
        const e = Array.isArray(session?.commits as any) ? (session!.commits as any).length : 0
        const prev = prevGraphCountsRef.current
        if (n === prev.n && e === prev.e) return
        prevGraphCountsRef.current = { n, e }
        if (canvasItems.length === 0) return
        // Default behavior: only place missing items (new nodes) on structure changes.
        // Do not reshuffle existing items on session entry.
        const { missing, brokenPlaceholderAuto } = getIdsNeedingPlacement()
        if (missing.length === 0 && brokenPlaceholderAuto.length === 0) return
        setLayoutMode('fill-missing')
        setNeedsLayout(true)
    }, [session?.nodes, session?.commits, canvasItems.length, getMissingPositionIds, getIdsNeedingPlacement])

    // Trigger layout when image natural sizes arrive (placeholder -> real)
    const prevSizesCountRef = useRef<number>(0)
    useEffect(() => {
        const count = Object.keys(imageSizesBySrc).length
        if (count <= prevSizesCountRef.current) return
        prevSizesCountRef.current = count
        if (canvasItems.length === 0) return
        // Sizes arriving can help place items that were missing positions (new nodes).
        // For already-positioned items, we keep saved positions stable by default.
        const { missing, brokenPlaceholderAuto } = getIdsNeedingPlacement()
        if (missing.length === 0 && brokenPlaceholderAuto.length === 0) return
        setLayoutMode('fill-missing')
        setNeedsLayout(true)
    }, [imageSizesBySrc, canvasItems.length, getMissingPositionIds, getIdsNeedingPlacement])

    // NOTE: savedPositions is now a persistent source-of-truth for *all* positions.
    // We intentionally do NOT trigger layout on savedPositions changes to avoid
    // feedback loops (layout -> setWorldRects -> persist -> savedPositions -> layout).

    // Debounced layout execution: batches all layout requests into single frame
    useEffect(() => {
        if (!needsLayout) return
        const rafId = requestAnimationFrame(() => {
            // In fill-missing mode, never persist placeholder-based rects.
            // Wait until we have natural sizes for all nodes we intend to place.
            if (layoutMode === 'fill-missing') {
                const { missing, brokenPlaceholderAuto } = getIdsNeedingPlacement()
                const toPlace = Array.from(new Set([...missing, ...brokenPlaceholderAuto]))
                if (toPlace.length > 0) {
                    const srcById = new Map(canvasItems.map((it) => [it.id, it.src] as const))
                    for (const id of toPlace) {
                        const src = srcById.get(id)
                        if (!src) continue
                        if (!imageSizesBySrc[src]) {
                            // Defer until sizes are known.
                            // Important: clear the pending flag so the "sizes arrived" effect can re-trigger.
                            setNeedsLayout(false)
                            return
                        }
                    }
                }
            }

            const autoRects = computeLayoutRects() as Record<string, Rect>
            if (!autoRects || Object.keys(autoRects).length === 0) {
                setNeedsLayout(false)
                return
            }

            // Hybrid layout: merge manual positions with auto-layout.
            // Default: keep any existing saved positions stable and only fill missing nodes.
            const mergedRects: Record<string, Rect> = {}
            const occupied: Rect[] = []

            const { missing, brokenPlaceholderAuto } = getIdsNeedingPlacement()
            const toPlaceSet = new Set<string>([...missing, ...brokenPlaceholderAuto])

            // First pass: seed merged with saved positions (manual + stable auto in fill-missing mode).
            for (const nodeId of Object.keys(autoRects)) {
                const savedPos = savedPositions?.[nodeId]
                if (savedPos?.manuallySet) {
                    // Use manual position (x, y, w, h from saved)
                    const r = {
                        x: savedPos.x,
                        y: savedPos.y,
                        w: savedPos.w,
                        h: savedPos.h,
                    }
                    mergedRects[nodeId] = r
                    occupied.push(r)
                } else if (layoutMode === 'fill-missing' && savedPos) {
                    // Keep existing saved (auto) positions stable unless user explicitly requests full layout.
                    // Except: if the saved position was a placeholder-based auto rect, we will re-place it.
                    if (!toPlaceSet.has(nodeId)) {
                        const r = {
                            x: savedPos.x,
                            y: savedPos.y,
                            w: savedPos.w,
                            h: savedPos.h,
                        }
                        mergedRects[nodeId] = r
                        occupied.push(r)
                    }
                } else {
                    // Defer placement to second pass (so we can avoid overlaps against occupied)
                }
            }

            // Second pass: place remaining nodes using auto-layout, shifting to avoid overlaps
            const idsToPlace = Object.keys(autoRects).filter((id) => !mergedRects[id])
            idsToPlace.sort((a, b) => {
                const ra = autoRects[a]
                const rb = autoRects[b]
                if (!ra && !rb) return 0
                if (!ra) return 1
                if (!rb) return -1
                if (ra.y !== rb.y) return ra.y - rb.y
                return ra.x - rb.x
            })

            const translation =
                layoutMode === 'fill-missing'
                    ? getFillMissingTranslation(Object.values(autoRects), occupied)
                    : { dx: 0, dy: 0 }

            for (const nodeId of idsToPlace) {
                const candidate = autoRects[nodeId]
                if (!candidate) continue
                const translatedCandidate = {
                    ...candidate,
                    x: candidate.x + translation.dx,
                    y: candidate.y + translation.dy,
                }
                const placed =
                    layoutMode === 'fill-missing'
                        ? placeWithoutOverlap(translatedCandidate, occupied)
                        : translatedCandidate
                mergedRects[nodeId] = placed
                occupied.push(placed)
            }

            setWorldRects(mergedRects)
            setNeedsLayout(false)
        })
        return () => cancelAnimationFrame(rafId)
    }, [
        needsLayout,
        computeLayoutRects,
        setWorldRects,
        savedPositions,
        layoutMode,
        canvasItems,
        imageSizesBySrc,
        getIdsNeedingPlacement,
    ])

    // Center newly added canvas items
    const prevCanvasIdsRef = useRef<string[]>([])
    const prevSessionIdForCenteringRef = useRef<string | null>(null)
    useEffect(() => {
        // Reset tracking when session changes so we don't compare IDs across sessions.
        if (prevSessionIdForCenteringRef.current !== sessionId) {
            prevSessionIdForCenteringRef.current = sessionId ?? null
            prevCanvasIdsRef.current = []
        }
        const currentIds = canvasItems.map((it) => it.id)
        const prevIds = prevCanvasIdsRef.current
        prevCanvasIdsRef.current = currentIds
        const added = currentIds.filter((id) => !prevIds.includes(id))
        if (added.length === 0) return
        if (!centerOnAdd) return
        // Skip panning for the very first population; initial fit handles centering
        if (prevIds.length === 0) return
        const focusId = added[added.length - 1]
        let raf = 0
        let tries = 0
        const tryCenter = () => {
            const el = containerRef.current
            const rect = worldRectsById[focusId]
            if (el && rect) {
                const r = el.getBoundingClientRect()
                const cx = rect.x + rect.w / 2
                const cy = rect.y + rect.h / 2
                // Keep current scale (if provided), just pan to center
                const scaleGuess = typeof currentScale === 'number' && isFinite(currentScale) && currentScale > 0 ? currentScale : 1
                const nx = r.width / 2 - (scaleGuess || 1) * cx
                const ny = r.height / 2 - (scaleGuess || 1) * cy
                setContentOffset({ x: Math.round(nx), y: Math.round(ny) })
                return
            }
            if (tries++ < 12) {
                raf = requestAnimationFrame(tryCenter)
            }
        }
        raf = requestAnimationFrame(tryCenter)
        return () => { if (raf) cancelAnimationFrame(raf) }
    }, [canvasItems, containerRef, worldRectsById, setContentOffset, setContentScale, currentScale])

    // On first layout presence, center view to content bounding box and fit scale.
    const hadRectsRef = useRef<boolean>(false)
    useEffect(() => {
        // New session: we want to refit once rects arrive.
        hadRectsRef.current = false
        setReady(false)
        setLayoutMode('fill-missing')
        prevGraphCountsRef.current = { n: 0, e: 0 }
        prevSizesCountRef.current = 0
    }, [sessionId])
    useEffect(() => {
        if (hadRectsRef.current) return
        let tries = 0
        let raf = 0
        const EPS = 0.5
        const attempt = () => {
            const ids = Object.keys(worldRectsById)
            if (ids.length === 0) {
                if (tries++ < 12) raf = requestAnimationFrame(attempt)
                return
            }
            const el = containerRef.current
            if (!el || el.clientWidth <= 0 || el.clientHeight <= 0) {
                if (tries++ < 12) raf = requestAnimationFrame(attempt)
                return
            }
            let minX = Number.POSITIVE_INFINITY
            let minY = Number.POSITIVE_INFINITY
            let maxX = Number.NEGATIVE_INFINITY
            let maxY = Number.NEGATIVE_INFINITY
            for (const id of ids) {
                const r = worldRectsById[id]
                if (!r) continue
                if (r.x < minX) minX = r.x
                if (r.y < minY) minY = r.y
                if (r.x + r.w > maxX) maxX = r.x + r.w
                if (r.y + r.h > maxY) maxY = r.y + r.h
            }
            if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
                if (tries++ < 12) raf = requestAnimationFrame(attempt)
                return
            }
            const bboxW = Math.max(1, maxX - minX)
            const bboxH = Math.max(1, maxY - minY)
            const rect = el.getBoundingClientRect()
            const fitScaleW = rect.width / (bboxW * 1.2)
            const fitScaleH = rect.height / (bboxH * 1.2)
            const rawScale = Math.max(0.2, Math.min(2.0, Math.min(fitScaleW, fitScaleH)))
            // Snap scale to device-pixel-friendly increments to reduce blur
            const dpr = (typeof window !== 'undefined' && (window.devicePixelRatio || 1)) || 1
            const targetScale = Math.max(0.2, Math.min(2.0, Math.round(rawScale * dpr) / dpr))
            const cx = minX + bboxW / 2
            const cy = minY + bboxH / 2
            const nx = Math.round(rect.width / 2 - targetScale * cx)
            const ny = Math.round(rect.height / 2 - targetScale * cy)
            // Only update if significantly different to reduce flicker
            if (Math.abs((currentScale || 1) - targetScale) > 0.001) {
                setContentScale(targetScale)
            }
            const px = (currentOffset?.x ?? 0)
            const py = (currentOffset?.y ?? 0)
            if (Math.abs(px - nx) >= EPS || Math.abs(py - ny) >= EPS) {
                setContentOffset({ x: nx, y: ny })
            }
            hadRectsRef.current = true
            setReady(true)
        }
        raf = requestAnimationFrame(attempt)
        return () => { if (raf) cancelAnimationFrame(raf) }
    }, [worldRectsById, containerRef, setContentScale, setContentOffset, currentScale, currentOffset])

    // Expose a manual layout trigger function
    const applyLayout = () => {
        if (canvasItems.length === 0) return
        // Manual "Auto layout": do a full Dagre pass but still respect manual overrides.
        setLayoutMode('full')
        setNeedsLayout(true)
    }

    return { ready, applyLayout }
}

export default useVerticalTreeLayout

function placeWithoutOverlap(candidate: Rect, occupied: Rect[]): Rect {
    const PAD = 16
    const STEP_Y = 40
    const MAX_ITERS = 300
    let next: Rect = { ...candidate }
    for (let i = 0; i < MAX_ITERS; i++) {
        if (!occupied.some((o) => rectsOverlap(next, o, PAD))) {
            return next
        }
        next = { ...next, y: next.y + STEP_Y }
    }
    return next
}

function rectsOverlap(a: Rect, b: Rect, pad: number): boolean {
    const ax1 = a.x - pad
    const ay1 = a.y - pad
    const ax2 = a.x + a.w + pad
    const ay2 = a.y + a.h + pad
    const bx1 = b.x - pad
    const by1 = b.y - pad
    const bx2 = b.x + b.w + pad
    const by2 = b.y + b.h + pad
    return ax1 < bx2 && ax2 > bx1 && ay1 < by2 && ay2 > by1
}

function getFillMissingTranslation(
    autoRects: Rect[],
    occupied: Rect[]
): { dx: number; dy: number } {
    if (autoRects.length === 0) return { dx: 0, dy: 0 }
    if (occupied.length === 0) return { dx: 0, dy: 0 }

    const autoBBox = computeBBox(autoRects)
    const occBBox = computeBBox(occupied)

    // Anchor the auto-layout coordinate system to the existing canvas space so
    // newly placed nodes don't jump to the global origin (0,0).
    return {
        dx: occBBox.minX - autoBBox.minX,
        dy: occBBox.minY - autoBBox.minY,
    }
}

function computeBBox(rects: Rect[]): { minX: number; minY: number } {
    let minX = Number.POSITIVE_INFINITY
    let minY = Number.POSITIVE_INFINITY
    for (const r of rects) {
        if (r.x < minX) minX = r.x
        if (r.y < minY) minY = r.y
    }
    if (!isFinite(minX)) minX = 0
    if (!isFinite(minY)) minY = 0
    return { minX, minY }
}


